import { useMemo } from 'react';
import { Form } from 'react-final-form';
import { InputAdornment, Typography } from '@mui/material';
import { default as Grid } from '@mui/material/Unstable_Grid2/Grid2';

import { TextField, Switches, OnChange } from '@occc-app/design-system';
import { ConfirmDialog, LinearProgress } from '@occc/ui-design-system';
import { parseNumber } from '@occc/ui-forms';

import { MonthlyPerformanceGoalFormValues } from './specific-goals-dialog.types';
import { validateMonthlyPerformanceGoal } from './specific-goals-dialog.validation';

type SpecificGoalDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: MonthlyPerformanceGoalFormValues) => Promise<void>;
  retailerNetworkGoals: Omit<MonthlyPerformanceGoalFormValues, 'inheritRetailerNetworkGoals'>;
  isSpecificGoalSet?: boolean;
  specificGoals: Omit<MonthlyPerformanceGoalFormValues, 'inheritRetailerNetworkGoals'>;
};

export const SpecificGoalDialog = ({
  open,
  onClose,
  onSubmit,
  retailerNetworkGoals,
  specificGoals,
  isSpecificGoalSet,
}: SpecificGoalDialogProps) => {
  const initialValues = useMemo(() => {
    if (isSpecificGoalSet) {
      return {
        ...specificGoals,
        inheritRetailerNetworkGoals: false,
      };
    }

    return {
      ...retailerNetworkGoals,
      inheritRetailerNetworkGoals: true,
    };
  }, [retailerNetworkGoals, isSpecificGoalSet, specificGoals]);

  return (
    <Form<MonthlyPerformanceGoalFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateMonthlyPerformanceGoal}
      render={({ handleSubmit, valid, dirty, submitting, values, form }) => {
        return (
          <ConfirmDialog
            dialogTitle='Set Site Specific Goals'
            onClose={onClose}
            open={open}
            maxWidth='xs'
            confirmButtonText='Set Goals'
            confirmButtonProps={{
              onClick: handleSubmit,
              disabled: !valid || !dirty || submitting,
            }}
          >
            {submitting && <LinearProgress />}

            <Grid container spacing={1}>
              <Grid xs={12}>
                <TextField
                  name='membersPerStore'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end' sx={{ ml: 1 }}>
                        per month
                      </InputAdornment>
                    ),
                  }}
                  label='New Members'
                  disabled={submitting || values.inheritRetailerNetworkGoals}
                  margin='normal'
                  fieldProps={{
                    parse: parseNumber,
                  }}
                />
              </Grid>
              <Grid xs={12}>
                <TextField
                  name='transactionsPerStore'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end' sx={{ ml: 1 }}>
                        per month
                      </InputAdornment>
                    ),
                  }}
                  label='New Transactions'
                  disabled={submitting || values.inheritRetailerNetworkGoals}
                  margin='normal'
                  fieldProps={{
                    parse: parseNumber,
                  }}
                />
              </Grid>
              <Grid xs={12} display='flex' justifyContent='space-between' alignItems='center'>
                <Typography>Inherit Retail Network Goals</Typography>
                <Switches name='inheritRetailerNetworkGoals' data={{ disabled: submitting }} />
                <OnChange name='inheritRetailerNetworkGoals'>
                  {(value: boolean) => {
                    // We have to reset the values to RN goals defaults if switcher is on
                    // and to specific goals/initial state if switcher is off
                    const membersPerStoreNewValue = value
                      ? retailerNetworkGoals.membersPerStore
                      : specificGoals.membersPerStore;
                    const transactionsPerStoreNewValue = value
                      ? retailerNetworkGoals.transactionsPerStore
                      : specificGoals.transactionsPerStore;

                    form.change('membersPerStore', membersPerStoreNewValue);
                    form.change('transactionsPerStore', transactionsPerStoreNewValue);
                  }}
                </OnChange>
              </Grid>
            </Grid>
          </ConfirmDialog>
        );
      }}
    />
  );
};
